import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './InstructorDashboard.css';
import './ReportDashboard.css';
import Sidebar from './Sidebar';
import Header from './Header';
import { apiFetch } from './api';
import HomePage from './HomePage';
import InstructorCourses from './InstructorCourses';
import InstructorExams from './InstructorExams';
import InstructorStudents from './InstructorStudents';
import InstructorStatistics from './InstructorStatistics';
import CourseQuestions from './CourseQuestions';
import StudyTopics from './StudyTopics';
import Assignments from './Assignments';
import { ActiveCourseProvider } from './ActiveCourseContext';
import ActiveCourseBar from './ActiveCourseBar';

function ProgramBaskanDashboard({ user, onLogout, isImpersonating = false, onReturnToAdmin }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState('home');
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth > 768);
  const lastPathnameRef = useRef(location.pathname);
  const [selectedExam, setSelectedExam] = useState(null);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setIsSidebarOpen(true);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getPageFromUrl = (pathname) => {
    if (pathname.startsWith('/program-baskan/')) {
      const page = pathname.replace('/program-baskan/', '');
      if (page === '' || page === 'home') return 'home';
      return page;
    }
    return 'home';
  };

  // URL değişince state senkronla
  useEffect(() => {
    if (location.pathname !== lastPathnameRef.current) {
      lastPathnameRef.current = location.pathname;
      const urlPage = getPageFromUrl(location.pathname);
      setActivePage(urlPage);
      localStorage.setItem('programBaskanActivePage', urlPage);
      setSelectedExam(null);
    }
  }, [location.pathname]);

  const handlePageChange = (page) => {
    setActivePage(page);
    setSelectedExam(null);
    const pagePath = `/program-baskan/${page}`;
    lastPathnameRef.current = pagePath;
    navigate(pagePath);
    if (isMobile) setIsSidebarOpen(false);
    localStorage.setItem('programBaskanActivePage', page);
  };

  const renderPageContent = () => {
    switch (activePage) {
      case 'home':
        return <HomePage userType="program_baskan" onPageChange={handlePageChange} />;
      case 'students':
        return <InstructorStudents instructorId={user?.id} />;
      case 'courses':
        return <InstructorCourses instructorId={user?.id} />;
      case 'exams':
        return <InstructorExams instructorId={user?.id} isProgramBaskan={true} />;
      case 'statistics':
        return <InstructorStatistics instructorId={user?.id} />;
      case 'sinav-raporlari':
        return <ProgramBaskanExamReports instructorId={user?.id} />;
      case 'calisma-konulari':
        return <StudyTopics userType="program_baskan" />;
      case 'odevler':
        return <Assignments userType="program_baskan" />;
      case 'sorular':
        return <CourseQuestions userType="program_baskan" />;
      case 'sinav-raporu':
        return selectedExam ? (
          <ProgramBaskanExamStudentsPage exam={selectedExam} onBack={() => setSelectedExam(null)} />
        ) : (
          <ProgramBaskanSinavRaporu onExamClick={setSelectedExam} />
        );
      case 'akademisyen-siralama':
        return <ProgramBaskanAkademisyenSiralama />;
      case 'sinav-katilim-ozeti':
        return <ProgramBaskanExamParticipationSummary />;
      default:
        return null;
    }
  };

  const academicPages = new Set([
    'students', 'courses', 'exams', 'statistics', 'calisma-konulari', 'odevler', 'sorular',
  ]);

  return (
    <ActiveCourseProvider instructorId={user?.id} role="program_baskan">
    <div className="instructor-dashboard">
      <Header
        user={user}
        onLogout={onLogout}
        onProfileClick={() => {}}
        onHomeClick={() => handlePageChange('home')}
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        title="KOSTÜ Sınav Modülü"
      />

      <Sidebar
        userType="program_baskan"
        activePage={activePage}
        onPageChange={handlePageChange}
        user={user}
        isImpersonating={isImpersonating}
        onReturnToAdmin={onReturnToAdmin}
        isOpen={isMobile ? isSidebarOpen : true}
        onClose={isMobile ? () => setIsSidebarOpen(false) : undefined}
      />

      <div className="dashboard-main">
        <div className={`dashboard-content ${activePage === 'home' ? 'full-height' : ''}`}>
          {activePage === 'home' ? (
            renderPageContent()
          ) : (
            <div className="dashboard-container">
              {academicPages.has(activePage) && <ActiveCourseBar />}
              {renderPageContent()}
            </div>
          )}
        </div>
      </div>
    </div>
    </ActiveCourseProvider>
  );
}

function ProgramBaskanExamReports({ instructorId }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedExam, setSelectedExam] = useState(null);
  const tabs = [
    { id: 'overview', label: 'Genel Bakış' },
    { id: 'participation', label: 'Katılım' },
    { id: 'statistics', label: 'İstatistikler' },
  ];

  if (selectedExam) {
    return <ProgramBaskanExamStudentsPage exam={selectedExam} onBack={() => setSelectedExam(null)} />;
  }

  return (
    <div className="report-tabs-page">
      <div className="report-tabs-header">
        <div>
          <h2 className="report-title">Sınav Raporları</h2>
          <p className="report-subtitle">Programınızdaki sınavların genel görünümü, katılımı ve istatistikleri.</p>
        </div>
      </div>
      <div className="report-tabs" role="tablist" aria-label="Sınav raporu görünümleri">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`report-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="report-tabs-content" role="tabpanel">
        {activeTab === 'overview' && <ProgramBaskanSinavRaporu onExamClick={setSelectedExam} />}
        {activeTab === 'participation' && <ProgramBaskanExamParticipationSummary />}
        {activeTab === 'statistics' && <InstructorStatistics instructorId={instructorId} useActiveCourseSelection={false} />}
      </div>
    </div>
  );
}

function ProgramBaskanSinavRaporu({ onExamClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCourses, setExpandedCourses] = useState({});
  const [expandedPrograms, setExpandedPrograms] = useState({});

  useEffect(() => {
    apiFetch('/program-baskan/dashboard')
      .then((r) => r.json())
      .then((res) => {
        if (res.success) {
          setData(res);
          // Tek program varsa varsayılan açık
          if (!res.multi_program) {
            setExpandedPrograms({ single: true });
          }
        } else {
          setError(res.message || 'Veri alınamadı');
        }
      })
      .catch(() => setError('Sunucu hatası'))
      .finally(() => setLoading(false));
  }, []);

  const toggleCourse = (courseId) => setExpandedCourses((p) => ({ ...p, [courseId]: !p[courseId] }));
  const toggleProgram = (pid) => setExpandedPrograms((p) => ({ ...p, [pid]: !p[pid] }));

  if (loading) return <div className="report-loading">Yükleniyor...</div>;
  if (error) return <div className="report-error">{error}</div>;

  // Normalize: her zaman programs dizisi üzerinden çalış
  const programs = data?.multi_program
    ? (data?.programs || [])
    : [{ program_id: data?.program_id, program_name: data?.program_name, courses: data?.courses || [], exam_count: (data?.courses || []).reduce((s, c) => s + c.exam_count, 0) }];

  const totalExams = programs.reduce((s, p) => s + (p.exam_count || 0), 0);
  const totalCourses = programs.reduce((s, p) => s + (p.courses || []).length, 0);

  return (
    <div className="report-wrapper">
      <div className="report-header-card">
        <div className="report-header-info">
          <h2 className="report-title">
            {programs.length === 1 ? programs[0].program_name : 'Programlarım'}
          </h2>
          <p className="report-subtitle">Bölüm Sınav Raporu</p>
        </div>
        <div className="report-stats-row">
          {programs.length > 1 && (
            <div className="report-stat-box">
              <span className="report-stat-number">{programs.length}</span>
              <span className="report-stat-label">Program</span>
            </div>
          )}
          <div className="report-stat-box">
            <span className="report-stat-number">{totalCourses}</span>
            <span className="report-stat-label">Ders</span>
          </div>
          <div className="report-stat-box">
            <span className="report-stat-number">{totalExams}</span>
            <span className="report-stat-label">Toplam Sınav</span>
          </div>
        </div>
      </div>

      {programs.map((program) => (
        <div key={program.program_id} className={programs.length > 1 ? 'report-program-block' : ''}>
          {programs.length > 1 && (
            <button
              className="report-program-header"
              onClick={() => toggleProgram(program.program_id)}
            >
              <div className="report-program-name">{program.program_name}</div>
              <div className="report-course-meta">
                <span className="report-badge-course">{(program.courses || []).length} Ders</span>
                <span className="report-badge-exam">{program.exam_count} Sınav</span>
                <span className={`report-chevron ${expandedPrograms[program.program_id] ? 'open' : ''}`}>▼</span>
              </div>
            </button>
          )}

          {(programs.length === 1 || expandedPrograms[program.program_id]) && (
            <div className={programs.length > 1 ? 'report-program-body' : ''}>
              {(program.courses || []).length === 0 ? (
                <div className="report-empty">Bu programa ait ders bulunamadı.</div>
              ) : (
                (program.courses || []).map((course) => (
                  <div key={course.course_id} className="report-course-block">
                    <button
                      className="report-course-header"
                      onClick={() => toggleCourse(course.course_id)}
                    >
                      <div className="report-course-name">
                        <span className="report-course-code">{course.course_code}</span>
                        {course.course_name}
                      </div>
                      <div className="report-course-meta">
                        <span className="report-badge-exam">{course.exam_count} Sınav</span>
                        <span className={`report-chevron ${expandedCourses[course.course_id] ? 'open' : ''}`}>▼</span>
                      </div>
                    </button>

                    {expandedCourses[course.course_id] && (
                      <div className="report-exam-table-wrapper">
                        {course.exams.length === 0 ? (
                          <div className="report-no-exam">Bu ders için henüz sınav yapılmamış.</div>
                        ) : (
                          <table className="report-exam-table">
                            <thead>
                              <tr>
                                <th>Sınav Adı</th>
                                <th>Mod</th>
                                <th>Tip</th>
                                <th>Durum</th>
                                <th>Başlangıç</th>
                                <th>Bitiş</th>
                                <th>Giren Öğrenci</th>
                              </tr>
                            </thead>
                            <tbody>
                              {course.exams.map((exam) => (
                                <tr key={exam.id}>
                                  <td
                                    className="report-exam-title"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => onExamClick && onExamClick(exam)}
                                    title="Sonuçları görüntüle"
                                  >
                                    {exam.title}
                                  </td>
                                  <td>{exam.mode === 'Online' ? 'Online' : 'Fiziksel'}</td>
                                  <td>{examTypeLabel(exam.type)}</td>
                                  <td><span className={`report-status-badge status-${(exam.status || '').toLowerCase()}`}>{examStatusLabel(exam.status)}</span></td>
                                  <td>{formatDate(exam.start_at)}</td>
                                  <td>{formatDate(exam.end_at)}</td>
                                  <td className="report-attempt-count">{exam.attempt_count}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function examTypeLabel(type) {
  if (type === 'Test') return 'Test';
  if (type === 'Classic') return 'Klasik';
  if (type === 'Mixed') return 'Karışık';
  return type || '-';
}

function examStatusLabel(status) {
  if (status === 'Draft') return 'Taslak';
  if (status === 'Published') return 'Yayında';
  if (status === 'Paused') return 'Duraklatıldı';
  if (status === 'Completed') return 'Tamamlandı';
  if (status === 'Archived') return 'Arşivlendi';
  return status || '-';
}

function formatDate(isoStr) {
  if (!isoStr) return '-';
  try {
    const d = new Date(isoStr);
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return isoStr;
  }
}

function ProgramBaskanAkademisyenSiralama() {
  const [akademisyenler, setAkademisyenler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    apiFetch('/program-baskan/akademisyen-siralama')
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setAkademisyenler(res.akademisyenler || []);
        else setError(res.message || 'Veri alınamadı');
      })
      .catch(() => setError('Sunucu hatası'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="report-loading">Yükleniyor...</div>;
  if (error) return <div className="report-error">{error}</div>;

  const siraliTam = [...akademisyenler];
  const filtered = siraliTam.filter((a) =>
    a.akademisyen_adi.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const maxExam = siraliTam[0]?.exam_count || 1;

  const rozetRenk = (rank) => {
    if (rank === 0) return { bg: '#fef9c3', border: '#fbbf24', text: '#92400e', medal: '🥇' };
    if (rank === 1) return { bg: '#f1f5f9', border: '#94a3b8', text: '#475569', medal: '🥈' };
    if (rank === 2) return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e', medal: '🥉' };
    return { bg: 'white', border: '#e2e8f0', text: '#1e293b', medal: null };
  };

  return (
    <div className="report-wrapper">
      <div className="report-header-card">
        <div className="report-header-info">
          <h2 className="report-title">Akademisyen Sıralama Tablosu</h2>
          <p className="report-subtitle">Bölümünüzdeki en aktif akademisyenler</p>
        </div>
        <div className="report-stats-row">
          <div className="report-stat-box">
            <span className="report-stat-number">{akademisyenler.length}</span>
            <span className="report-stat-label">Akademisyen</span>
          </div>
          <div className="report-stat-box">
            <span className="report-stat-number">
              {akademisyenler.reduce((s, a) => s + a.exam_count, 0)}
            </span>
            <span className="report-stat-label">Toplam Sınav</span>
          </div>
        </div>
      </div>

      <div className="akademisyen-search-row">
        <input
          className="akademisyen-search-input"
          type="text"
          placeholder="Akademisyen adıyla ara..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {akademisyenler.length === 0 ? (
        <div className="report-empty">Bölümünüzde henüz sınav oluşturmuş akademisyen bulunamadı.</div>
      ) : filtered.length === 0 ? (
        <div className="report-empty">"{searchQuery}" için sonuç bulunamadı.</div>
      ) : (
        <div className="report-exam-table-wrapper" style={{ marginTop: 0 }}>
          <table className="report-exam-table">
            <thead>
              <tr>
                <th style={{ width: 48, textAlign: 'center' }}>Sıra</th>
                <th>Akademisyen</th>
                <th style={{ textAlign: 'center' }}>Ders Sayısı</th>
                <th style={{ textAlign: 'center' }}>Sınav Sayısı</th>
                <th style={{ width: 140 }}>Aktivite</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const realRank = siraliTam.findIndex((x) => x.akademisyen_id === a.akademisyen_id);
                const { bg, border, text, medal } = rozetRenk(realRank);
                const barPct = Math.round((a.exam_count / maxExam) * 100);
                return (
                  <tr key={a.akademisyen_id} style={{ background: bg }}>
                    <td style={{ textAlign: 'center', fontWeight: 700, color: text, fontSize: '1rem' }}>
                      {medal ? medal : <span style={{ color: '#9ca3af' }}>{realRank + 1}</span>}
                    </td>
                    <td style={{ fontWeight: realRank < 3 ? 700 : 500, color: text }}>
                      {a.akademisyen_adi}
                    </td>
                    <td style={{ textAlign: 'center', color: '#4a5568' }}>{a.course_count}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span
                        className="report-badge-exam"
                        style={{
                          background: `rgba(76,175,80,${0.08 + 0.22 * (a.exam_count / maxExam)})`,
                          border: `1px solid ${border}`,
                          color: text,
                          fontSize: '0.82rem',
                          padding: '0.2rem 0.65rem',
                        }}
                      >
                        {a.exam_count}
                      </span>
                    </td>
                    <td>
                      <div className="akademisyen-bar-track">
                        <div className="akademisyen-bar-fill" style={{ width: `${barPct}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ProgramBaskanExamParticipationSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedExamId, setSelectedExamId] = useState(null);

  useEffect(() => {
    apiFetch('/program-baskan/exam-participation-summary')
      .then((response) => response.json())
      .then((result) => {
        if (!result.success) throw new Error(result.message || 'Veri alınamadı.');
        setData(result);
        setSelectedCategory(result.summary?.[0]?.exam_category || '');
        setSelectedExamId(null);
      })
      .catch((requestError) => setError(requestError.message || 'Sunucu hatası.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="report-loading">Sınav katılım verileri yükleniyor...</div>;
  if (error) return <div className="report-error">{error}</div>;

  const summary = data?.summary || [];
  const filteredParticipations = (data?.participations || []).filter(
    (participation) => participation.exam_category === selectedCategory
  );
  const selectedSummary = summary.find((item) => item.exam_category === selectedCategory);
  const exams = Object.values(filteredParticipations.reduce((byExam, participation) => {
    if (!byExam[participation.exam_id]) {
      byExam[participation.exam_id] = {
        exam_id: participation.exam_id,
        exam_title: participation.exam_title,
        course_code: participation.course_code,
        course_name: participation.course_name,
        academician_name: participation.academician_name,
        participant_count: 0,
      };
    }
    byExam[participation.exam_id].participant_count += 1;
    return byExam;
  }, {}));
  const selectedExam = exams.find((exam) => exam.exam_id === selectedExamId) || null;
  const selectedExamParticipations = selectedExam
    ? filteredParticipations.filter((participation) => participation.exam_id === selectedExam.exam_id)
    : [];

  return (
    <div className="report-wrapper participation-summary">
      <div className="report-header-card">
        <div className="report-header-info">
          <h2 className="report-title">Sınav Katılım Özeti</h2>
          <p className="report-subtitle">Yalnızca en az bir öğrencinin teslim ettiği sınavlar gösterilir.</p>
        </div>
        <div className="report-stats-row">
          <div className="report-stat-box">
            <span className="report-stat-number">{data?.total_exams || 0}</span>
            <span className="report-stat-label">Yapılan Sınav</span>
          </div>
          <div className="report-stat-box">
            <span className="report-stat-number">{data?.total_participations || 0}</span>
            <span className="report-stat-label">Toplam Katılım</span>
          </div>
          <div className="report-stat-box">
            <span className="report-stat-number">{summary.length}</span>
            <span className="report-stat-label">Sınav Türü</span>
          </div>
        </div>
      </div>

      {summary.length === 0 ? (
        <div className="report-empty">Henüz teslim edilmiş sınav verisi bulunmuyor.</div>
      ) : (
        <>
          <div className="participation-type-grid" aria-label="Sınav türleri">
            {summary.map((item) => (
              <button
                key={item.exam_category}
                type="button"
                className={`participation-type-card ${selectedCategory === item.exam_category ? 'active' : ''}`}
                onClick={() => {
                  setSelectedCategory(item.exam_category);
                  setSelectedExamId(null);
                }}
                aria-pressed={selectedCategory === item.exam_category}
              >
                <span className="participation-type-name">{item.exam_category}</span>
                <span className="participation-type-count">{item.exam_count}</span>
                <span className="participation-type-label">yapılan sınav</span>
                <span className="participation-type-meta">{item.student_count} öğrenci · {item.course_count} ders</span>
              </button>
            ))}
          </div>

          <div className="participation-detail-heading">
            <div>
              <h3>{selectedCategory} sınavları</h3>
              <p>{selectedSummary?.exam_count || 0} sınav · Bir sınava tıklayarak katılımcılarını görüntüleyin.</p>
            </div>
          </div>
          <div className="report-exam-table-wrapper" style={{ marginTop: 0 }}>
            <table className="report-exam-table">
              <thead>
                <tr>
                  <th>Sınav</th>
                  <th>Ders</th>
                  <th>Akademisyen</th>
                  <th style={{ textAlign: 'center' }}>Katılımcı</th>
                </tr>
              </thead>
              <tbody>
                {exams.map((exam) => (
                  <tr key={exam.exam_id} className={selectedExam?.exam_id === exam.exam_id ? 'participation-exam-row-selected' : ''}>
                    <td>
                      <button
                        type="button"
                        className="participation-exam-button"
                        onClick={() => setSelectedExamId(exam.exam_id)}
                        aria-pressed={selectedExam?.exam_id === exam.exam_id}
                      >
                        {exam.exam_title}
                      </button>
                    </td>
                    <td>{exam.course_code ? `${exam.course_code} · ` : ''}{exam.course_name}</td>
                    <td>{exam.academician_name || '-'}</td>
                    <td className="report-attempt-count">{exam.participant_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selectedExam && (
            <>
              <div className="participation-detail-heading participation-student-heading">
                <div>
                  <h3>{selectedExam.exam_title} katılımcıları</h3>
                  <p>{selectedExam.participant_count} öğrenci teslim etti.</p>
                </div>
                <button type="button" className="participation-back-button" onClick={() => setSelectedExamId(null)}>
                  Sınav listesine dön
                </button>
              </div>
              <div className="report-exam-table-wrapper" style={{ marginTop: 0 }}>
                <table className="report-exam-table">
                  <thead>
                    <tr>
                      <th>Öğrenci No</th>
                      <th>Öğrenci</th>
                      <th>Teslim Tarihi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedExamParticipations.map((item) => (
                      <tr key={`${item.exam_id}-${item.student_number}`}>
                        <td>{item.student_number}</td>
                        <td style={{ fontWeight: 500 }}>{item.student_name || '-'}</td>
                        <td>{formatDate(item.submitted_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export { ProgramBaskanSinavRaporu };
export default ProgramBaskanDashboard;


function ProgramBaskanExamStudentsPage({ exam, onBack }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isFiziksel = exam?.mode !== 'Online';

  useEffect(() => {
    if (isFiziksel) { setLoading(false); return; }
    apiFetch(`/program-baskan/exams/${exam.id}/students`)
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setStudents(res.students || []);
        else setError(res.message || 'Öğrenci listesi alınamadı.');
      })
      .catch(() => setError('Sunucu hatası.'))
      .finally(() => setLoading(false));
  }, [exam?.id, isFiziksel]);

  return (
    <div className="report-wrapper">
      <div className="report-header-card">
        <div className="report-header-info">
          <button onClick={onBack} className="exam-students-back-btn">← Geri</button>
          <h2 className="report-title" style={{ marginTop: '0.5rem' }}>{exam?.title}</h2>
          <p className="report-subtitle">
            {exam?.mode === 'Online' ? 'Online' : 'Fiziksel'} · {examTypeLabel(exam?.type)} ·{' '}
            <span className={`report-status-badge status-${(exam?.status || '').toLowerCase()}`}>
              {examStatusLabel(exam?.status)}
            </span>
          </p>
        </div>
        {!loading && !error && !isFiziksel && (
          <div className="report-stats-row">
            <div className="report-stat-box">
              <span className="report-stat-number">{students.length}</span>
              <span className="report-stat-label">Toplam Öğrenci</span>
            </div>
            <div className="report-stat-box">
              <span className="report-stat-number">{students.filter(s => s.status === 'Submitted').length}</span>
              <span className="report-stat-label">Sınava Giren</span>
            </div>
            <div className="report-stat-box">
              <span className="report-stat-number">{students.filter(s => s.status !== 'Submitted').length}</span>
              <span className="report-stat-label">Girmeyen</span>
            </div>
          </div>
        )}
      </div>

      {loading && <div className="report-loading">Yükleniyor...</div>}
      {error && <div className="report-error">{error}</div>}
      {!loading && !error && isFiziksel && (
        <div className="report-empty">Bu fiziksel sınava ait online sonuç bulunmamaktadır.</div>
      )}
      {!loading && !error && !isFiziksel && students.length === 0 && (
        <div className="report-empty">Bu derse kayıtlı öğrenci bulunamadı.</div>
      )}
      {!loading && !error && !isFiziksel && students.length > 0 && (
        <div className="report-exam-table-wrapper" style={{ borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <table className="report-exam-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Öğrenci No</th>
                <th>Öğrenci Adı</th>
                <th>Durum</th>
                <th>Puan</th>
                <th>Başlangıç</th>
                <th>Bitiş</th>
                <th>İhlal</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s, i) => {
                const girdi = s.status === 'Submitted';
                return (
                  <tr key={s.attempt_id || s.student_number} style={{ opacity: girdi ? 1 : 0.6 }}>
                    <td style={{ color: '#9ca3af', textAlign: 'center' }}>{i + 1}</td>
                    <td>{s.student_number}</td>
                    <td style={{ fontWeight: 500 }}>{s.student_name}</td>
                    <td>
                      <span className={`report-status-badge ${girdi ? 'status-completed' : 'status-draft'}`}>
                        {girdi ? 'Teslim Etti' : 'Girmedi'}
                      </span>
                    </td>
                    <td className="report-attempt-count" style={{ color: girdi ? '#4CAF50' : '#9ca3af' }}>
                      {girdi ? s.total_points : '-'}
                    </td>
                    <td>{formatDate(s.started_at)}</td>
                    <td>{formatDate(s.submitted_at)}</td>
                    <td style={{ textAlign: 'center', color: s.violation_count > 0 ? '#dc2626' : '#9ca3af' }}>
                      {girdi ? s.violation_count : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
